<template>
  <CloseHeader :title="`${t('label_user_management')}`" />

  <div class="app-container one-page">
    <div>
      <div class="us-tit-wrap">
        <h1 class="app-page-tit">{{ t('label_extra_users') }}</h1>
        <div
          ref="addUserRef"
          v-click-outside="handleClickOutsideAddUser"
          class="help-pop-con"
          :class="{ open: isVisible.addUser }"
        >
          <button type="button" class="help-btn" @click="toggleHelp('addUser')">
            <img :src="getAssetImageUrl('common/icons/help.svg')" alt="help" />
          </button>
          <div class="help-pop">
            <span>{{ t('description_additional_user_register') }}</span>
          </div>
        </div>
      </div>

      <div class="um-first-con">
        <span class="badge3">{{ t('label_register_organization_id') }}</span>
        <div class="us-sub-txt">
          <div class="help-felx">
            <p>{{ t('register_organization_id') }}</p>
            <div
              ref="createIdRef"
              v-click-outside="handleClickOutsideCreateId"
              class="help-pop-con"
              :class="{ open: isVisible.createId }"
            >
              <button type="button" class="help-btn" @click="toggleHelp('createId')">
                <img :src="getAssetImageUrl('common/icons/help.svg')" alt="help" />
              </button>
              <div class="help-pop">
                <span>{{ t('organization_id_tooltip') }}</span>
              </div>
            </div>
          </div>
          <span>
            {{ t('need_organization_id') }} <br />
            {{ t('once_create_not_change') }}
          </span>
        </div>

        <div>
          <input
            v-model="teamId"
            v-cursor-end
            class="app-t-input"
            type="text"
            enterkeyhint="done"
            @keyup.enter.prevent="(e) => (e.target as HTMLInputElement).blur()"
          />
        </div>
      </div>
    </div>

    <div class="app-btn-wrap">
      <button type="button" class="primary-btn l-btn full-width-btn" @click="createTeamId">
        <span>{{ t('label_register') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { createUserTeam } from '@/api/apiService';
import CloseHeader from '@/components/domains/mobile/common/CloseHeader.vue';
import { usePopupStore } from '@/stores/popup';
import { useUserStore } from '@/stores/user';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';

const { t } = useI18n();
const router = useRouter();

const userStore = useUserStore();
const popupStore = usePopupStore();

const isVisible = ref({
  addUser: false,
  createId: false,
});
const teamId = ref<string>('');

const addUserRef = useTemplateRef('addUserRef');
const createIdRef = useTemplateRef('createIdRef');

const toggleHelp = (type: string) => {
  isVisible.value[type] = !isVisible.value[type];
};

const handleClickOutsideAddUser = () => {
  isVisible.value.addUser = false;
};

const handleClickOutsideCreateId = () => {
  isVisible.value.createId = false;
};

const createTeamId = async () => {
  const errorMessage = validate();
  if (errorMessage) {
    popupStore.openToast(t(errorMessage));

    return;
  }

  try {
    const { success, errorCode } = await createUserTeam(teamId.value);

    if (!success) {
      let errorTitle;
      if (errorCode === 1034) {
        errorTitle = 'error_organization_id_1';
      } else if (errorCode === 1035) {
        errorTitle = 'error_organization_id_2';
      }

      popupStore.openToast(t(errorTitle));
      return;
    }

    await userStore.fetchUserInfo();
    router.replace({ name: 'app-team-manage' });
  } catch (error) {
    console.error('조직 ID 등록 실패: ', error);
  }
};

const validate = (): string => {
  let errorMessage;

  if (!teamId.value) {
    errorMessage = 'login_select_organization_id';
  }
  if (teamId.value.length < 4 || teamId.value.length > 20) {
    errorMessage = 'alert_organization_length_limit';
  }

  return errorMessage;
};
</script>

<style scoped lang="scss">
.um-first-con {
  .badge3 {
    color: #0078fb;
    background: #e9f4ff;
  }
  .us-sub-txt {
    padding: 0.5rem 0 0.75rem;
    .help-felx {
      display: flex;
      align-items: center;
      justify-content: center;
      padding-bottom: 0.5rem;
    }
    p {
      white-space: nowrap;
      font-weight: 600;
    }
    span {
      font-size: $font-size-psmall;
      line-height: 140%;
    }
  }
}

.us-tit-wrap {
  display: flex;
  align-items: center;
  gap: $size-xs;
  width: 100%;
  padding: 1.5rem 0;
}
.help-pop-con {
  position: relative;
  width: 100%;
  .help-btn {
    padding: 0.25rem;
  }
  .help-pop {
    position: absolute;
    top: calc(100% + 10px);
    left: -90px;
    background: #fff;
    padding: 0.75rem 1rem;
    border-radius: $size-s;
    box-shadow: $shadow-big;
    transform: scale(0.1);
    opacity: 0;
    transition: all 0.3s ease;
    span {
      line-height: 130%;
      font-size: $font-size-psmall;
      word-break: keep-all;
      color: $color-gray-500;
    }
  }
  &.open {
    .help-pop {
      opacity: 1;
      transform: scale(1);
      z-index: 10;
    }
  }
}
</style>
